import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getActiveTheme } from './tokens';

export default function AlertDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busy = false,
  onConfirm,
  onCancel,
}) {
  const theme = getActiveTheme();
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!busy) onCancel && onCancel();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[6px]"
        onClick={() => {
          if (!busy) onCancel && onCancel();
        }}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="alertdialog-title"
        aria-describedby={description ? 'alertdialog-desc' : undefined}
        className="absolute right-1/2 translate-x-1/2 top-24 w-[520px] max-w-[96vw] rounded shadow-lg border p-4"
        style={{
          background: theme.surfaceElevated || theme.surface2 || theme.surface,
          borderColor: theme.border,
          color: theme.text,
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div id="alertdialog-title" className="text-lg font-semibold">{title || 'Confirm'}</div>
          <button
            type="button"
            className="p-2 rounded disabled:opacity-60"
            onClick={() => onCancel && onCancel()}
            disabled={busy}
            aria-label="Close"
            onMouseEnter={(e) => { e.currentTarget.style.background = theme.surface2; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            ✕
          </button>
        </div>

        {description && (
          <div id="alertdialog-desc" className="text-sm mt-2" style={{ color: theme.mutedText }}>
            {description}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            className="px-3 py-1 rounded text-sm border disabled:opacity-60"
            style={{ borderColor: theme.border, color: theme.text }}
            onClick={() => onCancel && onCancel()}
            disabled={busy}
            onMouseEnter={(e) => { e.currentTarget.style.background = theme.surface2; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            onClick={() => onConfirm && onConfirm()}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
